import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import api, { API } from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, Download } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";

export function Reports() {
  const { t, user } = useApp();
  const [sum, setSum] = useState(null);
  const [series, setSeries] = useState([]);
  const [zDate, setZDate] = useState(new Date().toISOString().slice(0, 10));
  const [zBusy, setZBusy] = useState(false);
  useEffect(() => {
    api.get("/reports/summary").then((r) => setSum(r.data));
    api.get("/reports/sales", { params: { days: 30 } }).then((r) => setSeries(r.data.series));
  }, []);
  const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

  const downloadZ = async () => {
    setZBusy(true);
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API}/reports/z-report/pdf?date=${zDate}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (err) { toast.error("Z-Report failed"); } finally { setZBusy(false); }
  };

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-3xl font-bold">{t("reports")}</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-4 rounded-2xl" data-testid="rpt-today"><div className="text-xs uppercase text-stone-500 tracking-widest">{t("today_sales")}</div><div className="font-mono text-2xl font-bold mt-1">{money(sum?.today_sales)}</div></Card>
        <Card className="p-4 rounded-2xl" data-testid="rpt-month"><div className="text-xs uppercase text-stone-500 tracking-widest">{t("month_sales")}</div><div className="font-mono text-2xl font-bold mt-1">{money(sum?.month_sales)}</div></Card>
        <Card className="p-4 rounded-2xl" data-testid="rpt-credit"><div className="text-xs uppercase text-stone-500 tracking-widest">{t("pending_credit")}</div><div className="font-mono text-2xl font-bold mt-1">{money(sum?.pending_credit)}</div></Card>
        {user?.role === "owner" && <Card className="p-4 rounded-2xl" data-testid="rpt-stock"><div className="text-xs uppercase text-stone-500 tracking-widest">{t("stock_value")}</div><div className="font-mono text-2xl font-bold mt-1">{money(sum?.stock_valuation)}</div></Card>}
      </div>

      <Card className="p-5 rounded-2xl border-[#166534]/20 bg-[#F0FDF4]" data-testid="z-report-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#166534] grid place-items-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-heading font-semibold text-lg">{t("z_report")}</div>
              <div className="text-xs text-stone-500">{t("z_report_hint")}</div>
            </div>
          </div>
          <div className="flex gap-2 items-center flex-wrap w-full sm:w-auto">
            <Input type="date" value={zDate} onChange={(e) => setZDate(e.target.value)} data-testid="z-date"
                   max={new Date().toISOString().slice(0, 10)} className="h-11 rounded-xl w-full sm:w-40 flex-1 sm:flex-none" />
            <Button onClick={downloadZ} disabled={zBusy} data-testid="z-download"
                    className="h-11 rounded-xl bg-[#166534] hover:bg-[#14532D] text-white font-semibold flex-1 sm:flex-none whitespace-nowrap">
              <Download className="w-4 h-4 mr-2" /> {t("download_z_report")}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-4 rounded-2xl" data-testid="rpt-chart">
        <div className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-3">Sales (last 30 days)</div>
        <div className="w-full h-72">
          <ResponsiveContainer>
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Line type="monotone" dataKey="amount" stroke="#B45309" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}

export function Settings() {
  const { t, lang, setLang, user, shops, activeShop, addShop, switchShop, deleteShop } = useApp();
  const [shopName, setShopName] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const isOwner = user?.role === "owner";

  const doAdd = async () => {
    if (!shopName.trim()) return;
    setBusy(true);
    try { await addShop(shopName.trim()); setShopName(""); } catch (e) {} finally { setBusy(false); }
  };
  const doSwitch = async (sid) => { try { await switchShop(sid); } catch (e) {} };
  const doDelete = async (sid) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await deleteShop(sid); } catch (e) {}
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="font-heading text-3xl font-bold">{t("settings")}</h1>
      <Card className="p-5 rounded-2xl">
        <div className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-2">{t("language")}</div>
        <div className="flex gap-2">
          <button onClick={() => setLang("en")} data-testid="settings-lang-en" className={`flex-1 h-14 rounded-xl font-semibold ${lang === "en" ? "bg-[#B45309] text-white" : "bg-stone-100 text-stone-700"}`}>{t("english")}</button>
          <button onClick={() => setLang("gu")} data-testid="settings-lang-gu" className={`flex-1 h-14 rounded-xl font-semibold font-gujarati text-lg ${lang === "gu" ? "bg-[#B45309] text-white" : "bg-stone-100 text-stone-700"}`}>{t("gujarati")}</button>
        </div>
      </Card>

      <Card className="p-5 rounded-2xl">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("shops")}</div>
            <div className="text-sm text-stone-500 mt-0.5">{t("current_shop")}: <span className="font-semibold text-stone-800">{activeShop?.name}</span></div>
          </div>
        </div>
        <div className="space-y-2" data-testid="shops-list">
          {shops.map((s) => (
            <div key={s.id} data-testid={`shop-row-${s.id}`} className={`flex items-center justify-between p-3 rounded-xl border ${s.id === user?.active_shop_id ? "bg-[#FFF7ED] border-amber-200" : "border-stone-200"}`}>
              <div className="font-medium">{s.name}</div>
              <div className="flex gap-2">
                {s.id !== user?.active_shop_id && (
                  <button onClick={() => doSwitch(s.id)} data-testid={`shop-switch-${s.id}`} className="h-9 px-3 rounded-lg bg-[#166534] text-white text-xs font-medium">{t("switch_shop")}</button>
                )}
                {isOwner && shops.length > 1 && (
                  <button onClick={() => doDelete(s.id)} data-testid={`shop-delete-${s.id}`} className="h-9 px-3 rounded-lg bg-red-50 text-red-600 text-xs font-medium">{t("delete")}</button>
                )}
              </div>
            </div>
          ))}
        </div>
        {isOwner && (
          <div className="flex gap-2 mt-3">
            <input value={shopName} onChange={(e) => setShopName(e.target.value)} placeholder={t("shop_name")} data-testid="new-shop-name" className="flex-1 h-12 rounded-lg border border-stone-300 px-3 text-sm" />
            <button onClick={doAdd} disabled={busy || !shopName.trim()} data-testid="new-shop-add" className="h-12 px-4 rounded-xl bg-[#B45309] hover:bg-[#92400E] text-white font-semibold text-sm disabled:opacity-50">
              {t("add_shop")}
            </button>
          </div>
        )}
      </Card>

      <Card className="p-5 rounded-2xl">
        <div className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-2">{t("signed_in_as")}</div>
        <div className="font-heading text-lg font-semibold">{user?.name}</div>
        <div className="text-sm text-stone-500">{user?.email} • {user?.phone}</div>
        <div className="mt-2 inline-block text-[10px] uppercase font-bold text-[#B45309] bg-[#FFF7ED] px-2 py-0.5 rounded">{user?.role}</div>
      </Card>

      {isOwner && <AddUserCard />}
      {isOwner && <UsersListCard />}
      {isOwner && <SyncDebugCard />}
    </div>
  );
}

function SyncDebugCard() {
  const { t } = useApp();
  const [open, setOpen] = React.useState(false);
  const [log, setLog] = React.useState([]);
  React.useEffect(() => {
    if (!open) return;
    const refresh = () => import("@/api").then((m) => setLog([...m.apiLog]));
    refresh();
    window.addEventListener("kirana:api-log", refresh);
    return () => window.removeEventListener("kirana:api-log", refresh);
  }, [open]);
  return (
    <Card className="p-5 rounded-2xl" data-testid="sync-debug-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("sync_debug")}</div>
          <div className="text-xs text-stone-500 mt-1">{t("sync_debug_help")}</div>
        </div>
        <button onClick={() => setOpen((v) => !v)} data-testid="sync-debug-toggle"
                className={`h-9 px-3 rounded-lg text-xs font-semibold ${open ? "bg-[#B45309] text-white" : "bg-stone-100 text-stone-700"}`}>
          {open ? t("hide") : t("show")}
        </button>
      </div>
      {open && (
        <div className="mt-3 max-h-72 overflow-y-auto space-y-1 font-mono text-[11px]" data-testid="sync-debug-log">
          {log.length === 0 && <div className="text-stone-500 py-2 text-center">— {t("no_data")} —</div>}
          {log.map((e, i) => (
            <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-stone-50 border border-stone-100">
              <span className="text-stone-400 shrink-0">{e.ts.slice(11, 19)}</span>
              <span className={`shrink-0 w-8 font-bold ${e.method === "GET" ? "text-blue-700" : e.method === "DELETE" ? "text-red-700" : "text-[#B45309]"}`}>{e.method}</span>
              <span className={`shrink-0 w-8 ${e.status >= 400 || e.status === 0 ? "text-red-600" : "text-[#166534]"}`}>{e.status || "!"}</span>
              <span className="flex-1 min-w-0 truncate text-stone-700" title={e.url}>{e.url}</span>
              <span className="text-stone-400 shrink-0">{e.ms}ms</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function UsersListCard() {
  const { t } = useApp();
  const [users, setUsers] = React.useState([]);
  const [resetId, setResetId] = React.useState(null);
  const [pw, setPw] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const load = () => api.get("/users/mine").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  React.useEffect(() => { load(); }, []);
  const doReset = async () => {
    if (pw.length < 4) return toast.error(t("pw_min"));
    setBusy(true);
    try {
      await api.post(`/users/${resetId}/password`, { new_password: pw });
      toast.success(t("pw_updated"));
      setResetId(null); setPw("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-5 rounded-2xl" data-testid="users-list-card">
      <div className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-3">{t("users_list")}</div>
      <div className="space-y-2">
        {users.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.id}`} className="flex items-center justify-between gap-2 p-3 rounded-xl border border-stone-200">
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">{u.name} <span className="text-[10px] font-bold uppercase text-[#B45309] ml-1">{u.role}</span></div>
              <div className="text-xs text-stone-500 truncate">{u.email || "—"} · {u.phone || "—"}</div>
            </div>
            <button onClick={() => { setResetId(u.id); setPw(""); }} data-testid={`user-reset-pw-${u.id}`}
                    className="h-9 px-3 rounded-lg bg-stone-100 hover:bg-stone-200 text-xs font-semibold text-stone-700 shrink-0">
              {t("reset_password")}
            </button>
          </div>
        ))}
        {users.length === 0 && <div className="text-sm text-stone-500 py-4 text-center">—</div>}
      </div>
      {resetId && (
        <div className="mt-3 p-3 rounded-xl bg-[#FFF7ED] border border-[#B45309]/30 space-y-2" data-testid="pw-reset-panel">
          <div className="text-xs font-bold uppercase tracking-widest text-[#B45309]">{t("new_password")}</div>
          <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="pw-reset-input"
                 className="w-full h-11 rounded-lg border border-stone-300 px-3" placeholder={t("password")} />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setResetId(null)} data-testid="pw-reset-cancel" className="h-9 px-3 rounded-lg bg-stone-100 text-stone-700 text-xs font-semibold">{t("cancel")}</button>
            <button onClick={doReset} disabled={busy} data-testid="pw-reset-save" className="h-9 px-3 rounded-lg bg-[#166534] hover:bg-[#14532D] text-white text-xs font-semibold">{t("save")}</button>
          </div>
        </div>
      )}
    </Card>
  );
}

function AddUserCard() {
  const { t } = useApp();
  const [form, setForm] = React.useState({ name: "", email: "", phone: "", password: "", role: "staff" });
  const [busy, setBusy] = React.useState(false);
  const submit = async () => {
    if (!form.name.trim() || !form.password.trim() || (!form.email.trim() && !form.phone.trim())) {
      return toast.error(t("add_user_missing"));
    }
    setBusy(true);
    try {
      await api.post("/auth/register", { ...form, shop_name: "" });
      toast.success(t("user_added").replace("{name}", form.name));
      setForm({ name: "", email: "", phone: "", password: "", role: "staff" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-5 rounded-2xl border-[#B45309]/20" data-testid="add-user-card">
      <div className="text-xs font-bold uppercase tracking-widest text-[#B45309] mb-3">{t("add_user")}</div>
      <div className="space-y-3">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">{t("name")}</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="add-user-name" className="w-full h-12 rounded-lg border border-stone-300 px-3" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">{t("email")}</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="add-user-email" className="w-full h-12 rounded-lg border border-stone-300 px-3" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">{t("phone")}</label>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="add-user-phone" className="w-full h-12 rounded-lg border border-stone-300 px-3" />
          </div>
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">{t("password")}</label>
          <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="add-user-password" className="w-full h-12 rounded-lg border border-stone-300 px-3" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">{t("role")}</label>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setForm({ ...form, role: "staff" })} data-testid="add-user-role-staff"
                    className={`h-12 rounded-lg font-semibold text-sm ${form.role === "staff" ? "bg-[#B45309] text-white" : "bg-stone-100 text-stone-700"}`}>
              {t("role_staff")}
            </button>
            <button type="button" onClick={() => setForm({ ...form, role: "owner" })} data-testid="add-user-role-owner"
                    className={`h-12 rounded-lg font-semibold text-sm ${form.role === "owner" ? "bg-[#B45309] text-white" : "bg-stone-100 text-stone-700"}`}>
              {t("role_owner")}
            </button>
          </div>
        </div>
        <button onClick={submit} disabled={busy} data-testid="add-user-submit"
                className="w-full h-12 rounded-xl bg-[#166534] hover:bg-[#14532D] disabled:bg-stone-300 text-white font-semibold">
          {busy ? "…" : t("create_user")}
        </button>
      </div>
    </Card>
  );
}
